/* oxlint-disable tailwindcss/no-unknown-classes -- Styles are colocated in home-new.css. */
import { useEffect, useMemo, useRef, useState, type CSSProperties } from "react"

import { usePrefersReducedMotion } from "@/hooks/usePrefersReducedMotion"

import { cardRingClassName, elevatedRingClassName } from "../homeNewStyles"
import { useSectionInView } from "../sectionInView"
import { useRevealOnce } from "../useRevealOnce"

// Claim-card illustrations for the Semantic Graph section of the new homepage.

interface Context {
  symbol: string
  decision: string
  tokens: string
}

interface RepoFile {
  path: string
  /** What the graph hands the agent when this file is relevant to the task. */
  context?: Context
}

// The repository as the agent sees it: mostly files it can skip, with a
// relevant one every third row.
const FILES: RepoFile[] = [
  { path: "api/routes.ts" },
  { path: "db/schema.sql" },
  {
    path: "auth/session.ts",
    context: {
      symbol: "refreshToken()",
      decision: "Rotate tokens on every refresh",
      tokens: "2.4k",
    },
  },
  { path: "ui/Button.tsx" },
  { path: "utils/date.ts" },
  {
    path: "billing/plan.ts",
    context: {
      symbol: "PlanTier",
      decision: "Annual plans renew on the signup date",
      tokens: "1.8k",
    },
  },
  { path: "jobs/cron.ts" },
  { path: "ui/Modal.tsx" },
  {
    path: "lib/retry.ts",
    context: {
      symbol: "withBackoff()",
      decision: "Cap retries at three to stay under rate limits",
      tokens: "3.1k",
    },
  },
  { path: "lib/http.ts" },
  { path: "docs/adr-014.md" },
  {
    path: "web/checkout.tsx",
    context: {
      symbol: "submitOrder()",
      decision: "Charge only after stock is reserved",
      tokens: "2.1k",
    },
  },
  { path: "workers/sync.ts" },
  { path: "cli/index.ts" },
  {
    path: "search/rank.ts",
    context: {
      symbol: "scoreResult()",
      decision: "Rank recent sessions above old commits",
      tokens: "2.7k",
    },
  },
]

const ROW_PX = 24
const PITCH_PX = 30 // row plus the 6px gap
const LOOP_PX = FILES.length * PITCH_PX
/** Scroll speed, in px per second. */
const SPEED = 26
/** How long the replaced values take to leave, including the stagger. */
const CARD_LEAVE_MS = 300
const START_INDEX = FILES.findIndex((file) => file.path === "auth/session.ts")

/** A labeled row, styled like the Agentic Search result cards. */
function ContextField({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-1 px-3 py-1.5">
      <p className="font-sans text-xs leading-4 text-(--neutral-500)">{label}</p>
      {children}
    </div>
  )
}

interface ContextLayer {
  key: number
  file: RepoFile
  leaving: boolean
}

/**
 * One value in the context card. When the lit file changes, the new value
 * slides in from the left as the old one slides out to the right, both
 * fading and blurring; `step` staggers the fields top to bottom.
 */
function SwapValue({
  layers,
  step,
  className,
  render,
}: {
  layers: ContextLayer[]
  step: number
  className: string
  render: (file: RepoFile) => React.ReactNode
}) {
  return (
    <div className="grid min-w-0">
      {layers.map((layer) => (
        <p
          key={layer.key}
          className={`min-w-0 [grid-area:1/1] ${className} ${
            layer.leaving ? "home-new-context-value-leave" : "home-new-context-value-enter"
          }`}
          style={{ animationDelay: `${step * 30}ms` }}
        >
          {render(layer.file)}
        </p>
      ))}
    </div>
  )
}

/** What the graph returns for the lit file: the file, its symbol, the decision behind it. */
function ContextCard({ layers }: { layers: ContextLayer[] }) {
  return (
    <div
      className={`min-w-0 flex-1 overflow-hidden rounded-lg bg-floating py-1 ${elevatedRingClassName}`}
    >
      <div className="flex items-center gap-2 px-3 py-1.5">
        <p className="shrink-0 text-sm leading-5 font-semibold tracking-[-0.01em] text-default">
          Context
        </p>
        <div className="ml-auto min-w-0">
          <SwapValue
            layers={layers}
            step={0}
            className="truncate text-right text-xs leading-5 text-muted"
            render={(file) => `${file.context!.tokens} tokens`}
          />
        </div>
      </div>
      <ContextField label="File">
        <SwapValue
          layers={layers}
          step={1}
          className="truncate font-mono text-xs leading-4 text-default"
          render={(file) => file.path}
        />
      </ContextField>
      <ContextField label="Symbol">
        <SwapValue
          layers={layers}
          step={2}
          className="truncate font-mono text-xs leading-4 text-default"
          render={(file) => file.context!.symbol}
        />
      </ContextField>
      <ContextField label="Decision">
        <SwapValue
          layers={layers}
          step={3}
          className="min-h-10 text-sm leading-5 tracking-[-0.01em] text-default"
          render={(file) => file.context!.decision}
        />
      </ContextField>
    </div>
  )
}

/**
 * "Retrieve context, not whole repositories": the repo's files scroll past on
 * the left without stopping, dimmed and running off the bottom of the card;
 * whenever a relevant one passes the middle it lights up in the badge blue and
 * the context card on the right swaps to what the graph returns for it.
 */
export function ContextRetrievalIllustration() {
  const [revealed, ref] = useRevealOnce()

  const listRef = useRef<HTMLDivElement>(null)
  const trackRef = useRef<HTMLDivElement>(null)
  const [lit, setLit] = useState(START_INDEX)
  const [cards, setCards] = useState<ContextLayer[]>(() => [
    { key: 0, file: FILES[START_INDEX]!, leaving: false },
  ])

  // Where the scroll stopped when the section went offscreen, so it resumes
  // from there instead of jumping back to the start.
  const inView = useSectionInView()
  const resume = useRef<{ position: number; litRow: number; nextKey: number } | null>(null)

  const prefersReducedMotion = usePrefersReducedMotion()
  // Scroll constantly once revealed and in view. Each frame moves the track
  // directly; React only hears about it when the lit row changes.
  useEffect(() => {
    const list = listRef.current
    const track = trackRef.current
    if (!list || !track) return
    // Row `index` centered in the list.
    const centeredAt = (index: number) => index * PITCH_PX + ROW_PX / 2 - list.clientHeight / 2
    const place = (position: number) => {
      track.style.transform = `translate3d(0, ${-position}px, 0)`
    }
    const saved = resume.current
    place(saved ? saved.position : centeredAt(START_INDEX))
    if (!revealed || !inView) return
    if (prefersReducedMotion) return

    let position = saved ? saved.position : (centeredAt(START_INDEX) + LOOP_PX) % LOOP_PX
    let last = performance.now()
    let litRow = saved ? saved.litRow : START_INDEX
    let nextKey = saved ? saved.nextKey : 1
    let frame = 0
    const timers: number[] = []

    const tick = (now: number) => {
      position = (position + ((now - last) * SPEED) / 1000) % LOOP_PX
      last = now
      place(position)

      const middle = position + list.clientHeight / 2
      const row = Math.round((middle - ROW_PX / 2) / PITCH_PX)
      const distance = Math.abs(row * PITCH_PX + ROW_PX / 2 - middle)
      const file = FILES[row % FILES.length]!
      // A relevant row stays lit while it's within a row of the middle.
      const nextLit = file.context && distance <= PITCH_PX ? row : -1
      if (nextLit !== litRow) {
        const lighting = nextLit >= 0 && nextLit % FILES.length !== litRow % FILES.length
        litRow = nextLit
        setLit(nextLit)
        if (lighting) {
          const key = nextKey++
          setCards((current) => [
            ...current.filter((card) => !card.leaving).map((card) => ({ ...card, leaving: true })),
            { key, file, leaving: false },
          ])
          timers.push(
            window.setTimeout(
              () => setCards((current) => current.filter((card) => !card.leaving)),
              CARD_LEAVE_MS,
            ),
          )
        }
      }
      frame = window.requestAnimationFrame(tick)
    }
    frame = window.requestAnimationFrame(tick)
    return () => {
      window.cancelAnimationFrame(frame)
      timers.forEach((timer) => window.clearTimeout(timer))
      resume.current = { position, litRow, nextKey }
    }
  }, [revealed, inView, prefersReducedMotion])

  return (
    <div
      ref={ref}
      data-revealed={revealed ? "true" : "false"}
      className="home-new-context mx-auto -mb-6 flex min-h-[268px] w-full max-w-[420px] flex-1 items-center gap-5 font-sans"
      aria-hidden="true"
    >
      {/* The repository, scrolling past and off the bottom edge. */}
      <div
        ref={listRef}
        className="home-new-context-list relative w-[calc(50%-38px)] shrink-0 self-stretch"
      >
        <div ref={trackRef} className="absolute inset-x-0 top-0 flex flex-col gap-1.5 px-px">
          {[...FILES, ...FILES].map((file, row) => (
            <div
              key={row}
              data-picked={row === lit ? "true" : "false"}
              className={`home-new-context-file flex h-6 shrink-0 items-center rounded-sm px-2 ${cardRingClassName}`}
            >
              <p className="min-w-0 flex-1 truncate font-mono text-xs leading-4">{file.path}</p>
            </div>
          ))}
        </div>
      </div>

      <ContextCard layers={cards} />
    </div>
  )
}

// "Up to 71% fewer tokens": the repo as a field of tokens, three overlapping islands
// of small grey squares that fade out toward their edges. Only a few tokens
// are ever relevant: small clusters of related ones light up blue across the
// field and fade, so a handful glow at once.

const TOKEN_SIZE_PX = 10
const TOKEN_GAP_PX = 4
const TOKEN_FIELD_PX = 244
const TOKEN_ROWS = Math.floor((TOKEN_FIELD_PX + TOKEN_GAP_PX) / (TOKEN_SIZE_PX + TOKEN_GAP_PX))
const TOKEN_LIFE_MS = 3200
/** Clusters lit at once, roughly. */
const TOKEN_CLUSTERS = 8
/** Squares fainter than this never light up, so blue stays inside the islands. */
const TOKEN_LIGHTABLE = 0.7

/** Island centers and radii, as fractions of the field from its center. */
const ISLANDS: Array<[number, number, number, number]> = [
  [-0.42, -0.18, 0.5, 0.62],
  [0.2, 0.22, 0.55, 0.58],
  [0.58, -0.38, 0.34, 0.42],
]

/** A stable pseudo-random 0–1 for a square, the same on server and client. */
function noiseAt(row: number, column: number, seed = 0) {
  let x =
    Math.imul(row + 1, 374761393) ^
    Math.imul(column + 1, 668265263) ^
    Math.imul(seed + 1, 2246822519)
  x = Math.imul(x ^ (x >>> 13), 1274126177)
  return ((x ^ (x >>> 16)) >>> 0) / 4294967295
}

/**
 * How strongly the square at (row, column) shows: 1 inside an island, fading
 * and thinning out across its edge, 0 outside.
 */
function tokenFade(row: number, column: number, columns: number) {
  const x = columns > 1 ? (column - (columns - 1) / 2) / ((columns - 1) / 2) : 0
  const y = (row - (TOKEN_ROWS - 1) / 2) / ((TOKEN_ROWS - 1) / 2)
  const inside = Math.min(
    ...ISLANDS.map(([cx, cy, rx, ry]) => Math.hypot((x - cx) / rx, (y - cy) / ry)),
  )
  if (inside < 0.75) return 1
  if (inside > 1.15) return 0
  const edge = (inside - 0.75) / 0.4
  if (noiseAt(row, column) < edge * 0.5) return 0
  return 1 - edge * 0.85
}

const tokenKey = (row: number, column: number) => `${row}:${column}`

export function TokenFieldIllustration() {
  const [revealed, observe] = useRevealOnce()

  // Columns follow the card's width, so the field reflows instead of shrinking.
  const fieldRef = useRef<HTMLDivElement>(null)
  const [width, setWidth] = useState(418)
  useEffect(() => {
    const field = fieldRef.current
    if (!field) return
    const measure = () => setWidth(field.clientWidth)
    measure()
    const observer = new ResizeObserver(measure)
    observer.observe(field)
    return () => observer.disconnect()
  }, [])
  const columns = Math.floor((width + TOKEN_GAP_PX) / (TOKEN_SIZE_PX + TOKEN_GAP_PX))
  // Rebuilt only when the column count changes, not on every spawn.
  const cells = useMemo(() => {
    const grid: Array<{ row: number; column: number; fade: number }> = []
    for (let row = 0; row < TOKEN_ROWS; row++) {
      for (let column = 0; column < columns; column++) {
        grid.push({ row, column, fade: tokenFade(row, column, columns) })
      }
    }
    return grid
  }, [columns])

  const [lit, setLit] = useState<Array<{ key: string; born: number; delay: number }>>([])
  const [still, setStill] = useState(false)
  const lightable = useRef<Set<string>>(new Set())
  const lightableKeys = useMemo(
    () =>
      cells
        .filter((cell) => cell.fade >= TOKEN_LIGHTABLE)
        .map((cell) => tokenKey(cell.row, cell.column))
        .join(","),
    [cells],
  )
  useEffect(() => {
    lightable.current = new Set(lightableKeys.split(","))
  }, [lightableKeys, lightable])

  // Light a small cluster of related tokens every so often; each retires after
  // its pulse. Stops while the section is offscreen.
  const inView = useSectionInView()
  const prefersReducedMotion = usePrefersReducedMotion()
  useEffect(() => {
    if (!revealed || !inView) return
    if (prefersReducedMotion) {
      const frame = window.requestAnimationFrame(() => setStill(true))
      return () => window.cancelAnimationFrame(frame)
    }
    const spawn = () => {
      setLit((current) => {
        const now = performance.now()
        const alive = current.filter((token) => now - token.born < TOKEN_LIFE_MS)
        const taken = new Set(alive.map((token) => token.key))
        const free = [...lightable.current].filter((key) => !taken.has(key))
        const seed = free[Math.floor(Math.random() * free.length)]
        if (!seed) return alive
        const [row, column] = seed.split(":").map(Number) as [number, number]
        const neighbours = [
          tokenKey(row, column + 1),
          tokenKey(row + 1, column),
          tokenKey(row, column - 1),
          tokenKey(row - 1, column),
        ]
          .filter((key) => lightable.current.has(key) && !taken.has(key))
          .sort(() => Math.random() - 0.5)
        const picked = [seed, ...neighbours.slice(0, 1 + Math.floor(Math.random() * 2))]
        return [...alive, ...picked.map((key, index) => ({ key, born: now, delay: index * 120 }))]
      })
    }
    spawn()
    const timer = window.setInterval(spawn, TOKEN_LIFE_MS / TOKEN_CLUSTERS)
    return () => window.clearInterval(timer)
  }, [revealed, inView, lightable, prefersReducedMotion])

  const litByKey = new Map(lit.map((token) => [token.key, token]))

  return (
    <div
      ref={observe}
      data-revealed={revealed ? "true" : "false"}
      className="home-new-tokens mx-auto flex h-[244px] w-full max-w-[418px] items-center justify-center"
      aria-hidden="true"
    >
      <div ref={fieldRef} className="flex w-full justify-center">
        <div
          className="grid"
          style={{
            gridTemplateColumns: `repeat(${columns}, ${TOKEN_SIZE_PX}px)`,
            gridTemplateRows: `repeat(${TOKEN_ROWS}, ${TOKEN_SIZE_PX}px)`,
            gap: TOKEN_GAP_PX,
          }}
        >
          {cells.map(({ row, column, fade }) => {
            const key = tokenKey(row, column)
            if (fade === 0) return <span key={key} />
            const token = litByKey.get(key)
            // Resting dots only while motion stays reduced; `still` is left
            // behind when the preference flips back, and the clusters take over.
            const showStill =
              still &&
              prefersReducedMotion &&
              fade >= TOKEN_LIGHTABLE &&
              noiseAt(row, column, 7) > 0.93
            return (
              <span
                key={key}
                className="home-new-tokens-cell relative rounded-[2px]"
                style={{ "--token-row": row, "--token-fade": fade } as CSSProperties}
              >
                {token || showStill ? (
                  <span
                    key={token?.born ?? "still"}
                    className={`home-new-tokens-dot absolute inset-0 rounded-[2px] ${
                      showStill ? "home-new-tokens-dot-still" : ""
                    }`}
                    style={token ? { animationDelay: `${token.delay}ms` } : undefined}
                  />
                ) : null}
              </span>
            )
          })}
        </div>
      </div>
    </div>
  )
}
